import React, {createContext, PropsWithChildren, useContext, useEffect, useMemo, useState} from "react";
import {useTenantApi} from "../data/tenants";
import {useNavigate} from "react-router-dom";
import {useAsync} from "react-async-hook";
import {notBlank} from "../utils";
import {Alert, CircularProgress, Typography} from "@mui/material";
import {B2Tenant} from "../data/client.gen";

interface TenantContextProps {
	tenant: B2Tenant|undefined;
	setTenant: (tenant: B2Tenant) => void;
}

const TenantContext = createContext<TenantContextProps>({
	tenant: undefined,
	setTenant: () => {
		throw new Error("Context not properly initialized")
	}
});

function guessTenantId(): string {
	const host = window.location.hostname;
	switch (host) {
		case "booking.citylocker.at":
			return "citylocker";
		case "booking.citylocker.store":
			return "citylocker-store";
		case "balzklub.easylock.ch":
			return "balzklub";
		case "booking.citylockers.al":
		case "booking-dev.citylockers.al":
			return "city-lockers-ltd";
		case "booking.holdmybag.net":
			const pathname = window.location.pathname;
			if (pathname.startsWith(`/in-stuttgart`)) {
				return "in-stuttgart";
			}
			else if (pathname.startsWith("/d-live-duesseldorf")) {
				return "d-live-duesseldorf";
			}
			else if (pathname.startsWith("/acama-kreuzberg")) {
				return "acama-kreuzberg"
			}
			else if (pathname.startsWith("/elenas-apartments")) {
				return "elenas-apartments";
			}
	}
	return "variocube";
}

function TenantContextProvider({ children }: PropsWithChildren<{}>) {
	const navigate = useNavigate();
	const {getTenant} = useTenantApi();

	const [tenant, setTenant] = useState<B2Tenant>();

	const value = useMemo(() => ({ tenant, setTenant }), [tenant, setTenant]);

	useEffect(() => {
		if (window.location.pathname === '/' || window.location.pathname === '') {
			navigate(`/${guessTenantId()}`);
		}
	}, []);

	const {result, loading, error} = useAsync(async () => {
		try {
			const tenantId = window.location.pathname.split('/')[1];
			return (notBlank(tenantId))
				? getTenant(tenantId as string)
				: undefined;
		} catch (err) {
			console.error('Failed to load tenant', err);
			return undefined;
		}
	}, []);

	useEffect(() => {
		if (result) {
			setTenant(result);
		}
	}, [result]);

	return (
		<TenantContext.Provider value={value}>
			{loading && <CircularProgress />}
			{error && (
				<>
					<Typography variant="h5">Error loading tenant configuration.</Typography>
					<Alert severity="error">{error?.message ?? 'Error'}</Alert>
				</>
			)}
			{tenant !== undefined && (
				children
			)}
		</TenantContext.Provider>
	)
}

const useTenant = () => useContext(TenantContext);

export {
	TenantContextProvider,
	useTenant,
}
